import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {useTranslate} from '@computerrock/formation-i18n';
import {persistenceStates} from '@computerrock/formation-core';
import {Panel, useStyles, NoResultsBlock, Paginator, Divider, HighlightCircle, LoaderSpinner} from '@ace-de/ui-components';
import {historyIcon, Icon, infoAlertIcon} from '@ace-de/ui-components/icons';
import {withRouter, resolveRoute} from '@computerrock/formation-router';
import config from '../config';
import routePaths from '../routePaths';
import LogEntry from './ui-elements/LogEntry';
import * as caseLogSelectors from './caseLogSelectors';
import * as serviceCaseSelectors from '../service-cases/serviceCaseSelectors';
import * as invoiceSelectors from '../invoices/invoiceSelectors';

const CaseProtocolPanel = props => {
    const {cx} = useStyles();
    const {createTranslateShorthand} = useTranslate();
    const translatePanel = createTranslateShorthand('case_protocol_panel');
    const {history, serviceCase, invoice} = props;
    const {caseLogFilterResults, caseLogFilterResultsCount, caseLogFilterResultsPage} = props;
    const {caseLogFilterResultsPersistenceState} = props;

    const handleOnPaginationChange = page => {
        const searchParams = new URLSearchParams(history.location.search);
        searchParams.set('page', page);
        const queryParamsString = searchParams.toString();
        history.replace(resolveRoute(routePaths.INVOICE_OVERVIEW,
            {
                serviceCaseId: serviceCase.id,
                invoiceId: invoice.id,
            },
            {search: queryParamsString}));
    };

    return (
        <Fragment>
            <Panel title={translatePanel('panel_title.case_protocol')}>
                <Divider />
                <div className={cx('global!ace-u-padding--16-24-32')}>
                    {((caseLogFilterResultsPersistenceState === persistenceStates.READY
                    && caseLogFilterResults?.length === 0)
                    || caseLogFilterResultsPersistenceState === persistenceStates.FAILED) && (
                        <NoResultsBlock
                            colorVariant={caseLogFilterResultsPersistenceState === persistenceStates.FAILED
                                ? 'negative' : 'default'}
                            icon={(
                                <Icon
                                    className={cx('ace-c-icon--xxl', {
                                        'global!ace-c-icon--color-warning': caseLogFilterResultsPersistenceState === persistenceStates.FAILED,
                                    })}
                                    icon={caseLogFilterResultsPersistenceState === persistenceStates.FAILED
                                        ? infoAlertIcon
                                        : historyIcon}
                                />
                            )}
                            message={caseLogFilterResultsPersistenceState === persistenceStates.FAILED
                                ? translatePanel('results_error.message')
                                : translatePanel('no_results.message')}
                            description={caseLogFilterResultsPersistenceState === persistenceStates.FAILED
                                ? translatePanel('results_error.description')
                                : translatePanel('no_results.description')}
                        />
                    )}
                    {caseLogFilterResultsPersistenceState === persistenceStates.PENDING && (
                        <div
                            className={cx([
                                'global!ace-u-width--full',
                                'global!ace-u-height--full',
                                'global!ace-u-flex',
                                'global!ace-u-flex--align-center',
                                'global!ace-u-flex--justify-center',
                                'global!ace-u-flex--direction-column',
                            ])}
                        >
                            <HighlightCircle
                                className={cx([
                                    'ace-c-highlight-circle--medium',
                                    'ace-c-highlight-circle--primary-highlight',
                                    'global!ace-u-margin--top-64',
                                    'global!ace-u-margin--bottom-64',
                                ])}
                            >
                                <LoaderSpinner />
                            </HighlightCircle>
                            <p
                                className={cx([
                                    'global!ace-u-margin--top-16',
                                    'global!ace-u-margin--bottom-64',
                                    'global!ace-u-typography--variant-h4',
                                ])}
                            >
                                {translatePanel('loader_message.search_in_progress')}
                            </p>
                        </div>
                    )}
                    {caseLogFilterResultsPersistenceState === persistenceStates.READY
                        && caseLogFilterResults.length > 0
                        && caseLogFilterResults?.map((caseLog, idx) => (
                            <LogEntry key={idx} caseLog={caseLog} />
                        ))}
                </div>
            </Panel>
            <div>
                {!!caseLogFilterResultsCount && (
                    <Paginator
                        page={caseLogFilterResultsPage}
                        count={Math.ceil(caseLogFilterResultsCount / config.PROTOCOL_DEFAULT_PAGE_SIZE)}
                        onClick={page => handleOnPaginationChange(page)}
                    />
                )}
            </div>
        </Fragment>
    );
};

CaseProtocolPanel.propTypes = {
    serviceCase: PropTypes.object,
    history: PropTypes.object,
    invoice: PropTypes.object,
    caseLogFilterResults: PropTypes.array,
    caseLogFilterResultsCount: PropTypes.number,
    caseLogFilterResultsPage: PropTypes.number,
    caseLogFilterResultsPersistenceState: PropTypes.string,
};

CaseProtocolPanel.defaultProps = {
    serviceCase: null,
    history: null,
    invoice: null,
    caseLogFilterResults: [],
    caseLogFilterResultsCount: 0,
    caseLogFilterResultsPage: 0,
    caseLogFilterResultsPersistenceState: persistenceStates.PENDING,
};

const mapStateToProps = (state, props) => {
    const getServiceCase = serviceCaseSelectors.createServiceCaseSelector();
    const getInvoice = invoiceSelectors.createInvoicesSelector();

    return {
        serviceCase: getServiceCase(state, props),
        caseLogFilterResults: caseLogSelectors.getCaseLogFilterResults(state),
        caseLogFilterResultsCount: caseLogSelectors.getCaseLogFilterResultsCount(state),
        caseLogFilterResultsPage: caseLogSelectors.getCaseLogFilterResultsPage(state),
        invoice: getInvoice(state, props),
        caseLogFilterResultsPersistenceState: state.caseLogs.caseLogFilterResultsPersistenceState,
    };
};

export default withRouter(connect(mapStateToProps, null)(CaseProtocolPanel));
